import { Link } from 'react-router';
import { useClaimsStore } from '@/stores/claims-store';
import { useEffect } from 'react';
import { clsx } from 'clsx';

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  evidence_complete: 'Evidence Complete',
  pack_generated: 'Pack Ready',
  submitted: 'Submitted',
  awaiting_response: 'Awaiting Response',
  response_received: 'Response Received',
  escalated: 'Escalated',
  resolved: 'Resolved',
};

const statusColors: Record<string, string> = {
  draft: 'bg-surface-100 text-surface-800',
  evidence_complete: 'bg-brand-100 text-brand-700',
  pack_generated: 'bg-success-500/10 text-success-600',
  submitted: 'bg-brand-100 text-brand-700',
  awaiting_response: 'bg-warning-500/10 text-warning-600',
  response_received: 'bg-brand-100 text-brand-700',
  escalated: 'bg-error-500/10 text-error-600',
  resolved: 'bg-success-500/10 text-success-600',
};

// Format damage type for display (capitalize first letter)
const formatDamageType = (type: string): string => {
  const typeMap: Record<string, string> = {
    'tyre': 'Tyre',
    'wheel': 'Wheel',
    'suspension': 'Suspension',
    'bodywork': 'Bodywork',
    'steering': 'Steering',
    'other': 'Other',
  };
  return typeMap[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

export function ClaimsPage() {
  const { claims, isLoading, error, fetchClaims } = useClaimsStore();

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-brand-950">My Claims</h1>
          <p className="mt-2 text-surface-800">
            Manage and track all your pothole damage claims
          </p>
        </div>
        <Link
          to="/claims/new"
          className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
        >
          New Claim
          <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </Link>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-surface-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto" />
        </div>
      ) : error ? (
        <div className="bg-error-50 border border-error-200 rounded-xl p-8 text-center" role="alert">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="font-display text-xl font-semibold text-error-800 mb-2">
            We couldn't load your claims
          </h2>
          <p className="text-sm text-error-700 mb-6 max-w-md mx-auto">{error}</p>
          <button
            onClick={() => fetchClaims()}
            className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Try again
          </button>
        </div>
      ) : claims.length === 0 ? (
        <div className="bg-white rounded-xl border border-surface-200 p-12 text-center">
          <div className="w-20 h-20 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-semibold text-brand-950 mb-2">
            No claims yet
          </h2>
          <p className="text-surface-800 mb-6 max-w-md mx-auto">
            Start your first pothole damage claim. Mac will guide you through gathering
            evidence and preparing professional documentation.
          </p>
          <Link
            to="/claims/new"
            className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Start Your First Claim
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-50 border-b border-surface-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-brand-950">
                    Location
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-brand-950">
                    Date
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-brand-950">
                    Damage Type
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-brand-950">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-brand-950">
                    Cost
                  </th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                {claims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-surface-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-brand-950 truncate">
                            {claim.locationAddress || 'Location not set'}
                          </p>
                          <p className="text-sm text-surface-800 truncate">
                            {claim.locationPostcode || '—'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-surface-800">
                      {new Date(claim.incidentDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4">
                      {claim.damageType?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {claim.damageType.slice(0, 2).map((type) => (
                            <span
                              key={type}
                              className="px-2 py-0.5 bg-surface-100 text-surface-800 text-xs rounded"
                            >
                              {formatDamageType(type)}
                            </span>
                          ))}
                          {claim.damageType.length > 2 && (
                            <span className="px-2 py-0.5 bg-surface-100 text-surface-800 text-xs rounded">
                              +{claim.damageType.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-surface-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={clsx(
                          'inline-block px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                          statusColors[claim.status] || statusColors.draft
                        )}
                      >
                        {statusLabels[claim.status] || claim.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-surface-800">
                      {claim.repairCost
                        ? `£${Number(claim.repairCost).toFixed(2)}`
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/claims/${claim.id}`}
                        className="inline-flex items-center text-brand-600 hover:text-brand-700 font-medium text-sm"
                      >
                        View
                        <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
